import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppState } from '~/reducers';
import { reflectOperationStatusChange } from '~/domain/assets/store/hfs-operations/reducer';
import { useGetHfsOperationsStatusesQuery } from '~/api/assets-api/assets-api';
import { filterWsUpdatesPendingHfsOperations } from '~/domain/assets/utils/hfs-operations-utils';

const useHfsOperationsStatusSynchronizer = () => {
  const dispatch = useDispatch();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const wsReconnectionDate = useSelector((state: AppState) => state.app.wsReconnectionDate);
  const [prevReconnectionDate, setPrevReconnectionDate] = useState<Date | undefined>(
    wsReconnectionDate
  );
  const [skip, setSkip] = useState(true);
  const operations = useSelector((state: AppState) => state.assetHfsOperations.operations);
  const requestPayload = useMemo(
    () =>
      filterWsUpdatesPendingHfsOperations(operations ?? []).map((operation) => ({
        operationId: operation.operationId,
        iprId: operation.iprId,
      })),
    [operations]
  );
  const { data } = useGetHfsOperationsStatusesQuery(
    {
      operations: requestPayload,
    },
    {
      skip: requestPayload.length === 0 || skip,
    }
  );

  useEffect(() => {
    if (requestPayload.length === 0 && !skip) {
      setSkip(true);
    }
  }, [requestPayload.length, skip]);

  const handleOperationStatusUpdate = useCallback(
    (operation) => {
      dispatch(reflectOperationStatusChange(operation));
    },
    [dispatch]
  );

  useEffect(() => {
    if (data !== undefined) {
      setSkip(true);
      data.operationsStatuses.forEach(handleOperationStatusUpdate);
    }
  }, [data, handleOperationStatusUpdate]);

  useEffect(() => {
    if (wsReconnectionDate && wsReconnectionDate !== prevReconnectionDate) {
      // To ensure that we will not miss any status updates after this refetch
      setPrevReconnectionDate(wsReconnectionDate);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (requestPayload.length > 0) {
        timeoutRef.current = setTimeout(() => setSkip(false), 10000);
      }
    }
  }, [wsReconnectionDate, prevReconnectionDate, requestPayload]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [timeoutRef]);

  return {
    handleOperationStatusUpdate,
  };
};

export { useHfsOperationsStatusSynchronizer };
